import React from 'react';
import {
  Autocomplete,
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { DD_MM_YYYY } from '../../pages/Tasks/TaskConstants';

const STATUS_LABELS = {
  '': 'All',
  PENDING: 'Pending',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  CANCELLED: 'Cancelled',
};

/**
 * Senior Manager Leave inbox filters.
 * Employee options must come from the Reporting Manager reportee API
 * (GET /v1/additional-wfh/reportees) — not project-manager employees.
 */
const SeniorManagerLeaveFilters = ({
  filters,
  setFilters,
  reportees = [],
  leaveTypes = [],
}) => {
  const selectedEmp = reportees.find(
    (e) => String(e.id) === String(filters.employeeId),
  );
  const selectedLeave = leaveTypes.find(
    (l) => String(l.id) === String(filters.leaveId),
  );

  const empLabel = (emp) => {
    if (!emp) return 'All reportees';
    const name =
      `${emp.firstName || ''} ${emp.lastName || ''}`.trim() ||
      emp.employeeName ||
      emp.id;
    return emp.employeeCode != null ? `${name} (${emp.employeeCode})` : name;
  };

  return (
    <Box sx={{ mb: 3 }}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Start date"
              inputFormat={DD_MM_YYYY}
              value={filters.startDate ? dayjs(filters.startDate) : null}
              onChange={(value) =>
                setFilters((prev) => ({
                  ...prev,
                  startDate: value ? value.format('YYYY-MM-DD') : '',
                }))
              }
              renderInput={(params) => (
                <TextField {...params} fullWidth size="small" />
              )}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="End date"
              inputFormat={DD_MM_YYYY}
              value={filters.endDate ? dayjs(filters.endDate) : null}
              minDate={
                filters.startDate ? dayjs(filters.startDate) : undefined
              }
              onChange={(value) =>
                setFilters((prev) => ({
                  ...prev,
                  endDate: value ? value.format('YYYY-MM-DD') : '',
                }))
              }
              renderInput={(params) => (
                <TextField {...params} fullWidth size="small" />
              )}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel id="sm-leave-status-label" shrink>
              Status
            </InputLabel>
            <Select
              labelId="sm-leave-status-label"
              displayEmpty
              value={filters.status}
              label="Status"
              renderValue={(selected) => STATUS_LABELS[selected] || 'All'}
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, status: e.target.value }))
              }
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="PENDING">Pending</MenuItem>
              <MenuItem value="APPROVED">Approved</MenuItem>
              <MenuItem value="REJECTED">Rejected</MenuItem>
              <MenuItem value="CANCELLED">Cancelled</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Autocomplete
            fullWidth
            size="small"
            options={reportees}
            value={selectedEmp || null}
            getOptionLabel={(emp) => empLabel(emp)}
            isOptionEqualToValue={(option, value) =>
              String(option.id) === String(value.id)
            }
            onChange={(e, newValue) =>
              setFilters((prev) => ({
                ...prev,
                employeeId: newValue ? newValue.id : '',
              }))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Employee"
                placeholder="All reportees"
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <FormControl fullWidth size="small">
            <InputLabel id="sm-leave-type-label" shrink>
              Leave type
            </InputLabel>
            <Select
              labelId="sm-leave-type-label"
              displayEmpty
              value={filters.leaveId}
              label="Leave type"
              renderValue={() =>
                selectedLeave ? selectedLeave.leaveName : 'All'
              }
              onChange={(e) =>
                setFilters((prev) => ({ ...prev, leaveId: e.target.value }))
              }
            >
              <MenuItem value="">All</MenuItem>
              {leaveTypes.map((lt) => (
                <MenuItem key={lt.id} value={lt.id}>
                  {lt.leaveName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SeniorManagerLeaveFilters;
